import { esc, icon } from './format.ts';
import { viewOn } from './modules.ts';
import {
  administersNubi,
  buildsCourses,
  canAskKnowledge,
  canQuote,
  followsClaims,
  followsOnboarding,
  followsRenewals,
  has,
  isNubiOrg,
  me,
  runsCashBox,
  seesBackgroundChecks,
  seesPortfolio,
  supervises,
} from './session.ts';
import { showInMenu } from './sidebar.ts';

/**
 * The console's menu (#187): the options grouped by the agency's work, each group a disclosure
 * (a button with aria-expanded that opens and closes its options). Avisos stays outside the
 * groups so its count is always in sight. Each option shows only to the roles that work on it:
 * the API and Cerbos decide what a person may do; the menu only follows them.
 *
 * The model is the single source of the menu: the console builds the markup from it at boot
 * (`navHtml`), shows each person their part (`paintNav`) and opens the group of the screen on
 * view (`revealView`, from the router).
 *
 * Nubi's own organization has a menu model of its own (`PLATFORM_NAV`, «Administración de Nubi»,
 * ADR-0023) that replaces the agency's (`NAV`) for them; agency people never see it.
 */

export type NavItem = {
  view: string;
  icon: string;
  /** What the menu says; some options read differently for the person's own work. */
  label: () => string;
  /** Whether the signed-in person works on this screen (shapes the menu; the API decides). */
  sees: () => boolean;
  /** Carries the unread count (Avisos). */
  count?: boolean;
};
/** A group of options; its icon stands for it in the collapsed sidebar's rail (#188). */
export type NavGroup = { id: string; label: string; icon: string; items: NavItem[] };

const item = (
  view: string,
  label: string | (() => string),
  iconName: string,
  sees: () => boolean,
): NavItem => ({
  view,
  icon: iconName,
  label: typeof label === 'string' ? () => label : label,
  // A peripheral module turned off for the agency leaves its menu (ADR-0029).
  sees: () => viewOn(view) && sees(),
});

/** An agent with an agent code (her own commissions and goals), or management (everyone's). */
const ownOrAll = () => supervises() || (has('agent') && !!me?.agentId);
const own = () => !supervises();
const sells = () => has('agency_director', 'agency_admin', 'agent');

/**
 * Outside the groups, at the top of the menu: an agency's notices and each person's tasks (Nubi's
 * organization has neither).
 */
export const TOP: NavItem[] = [
  { ...item('avisos', 'Avisos', 'bell', () => !!me && !isNubiOrg()), count: true },
  // Each person's tasks (#317): every role of the agency, always on (no module turns it off).
  item('mis-tareas', 'Mis tareas', 'calendar', () => !!me && !isNubiOrg()),
];

export const NAV: NavGroup[] = [
  {
    id: 'analisis',
    label: 'Análisis',
    icon: 'trend',
    items: [
      item('resumen', 'Resumen', 'home', seesPortfolio),
      item('estadisticas', 'Estadísticas', 'chart', seesPortfolio),
      item('metas', () => (own() ? 'Mis metas' : 'Metas e incentivos'), 'target', ownOrAll),
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    icon: 'briefcase',
    items: [
      item('cotizar', 'Cotizar', 'quote', canQuote),
      item('prospectos', 'Prospectos', 'target', sells),
      item('clientes', 'Clientes', 'client', sells),
    ],
  },
  {
    id: 'polizas',
    label: 'Pólizas y cobro',
    icon: 'wallet',
    items: [
      item('polizas', 'Pólizas y recibos', 'policy', seesPortfolio),
      // The policies coming to their end and each renewal's follow-up (#308).
      item('renovaciones', 'Renovaciones', 'trend', followsRenewals),
      // The claims of the portfolio, their follow-up and the loss ratio (#309).
      item('siniestros', 'Siniestros', 'alert', followsClaims),
      item('caja', 'Caja', 'cash', () => runsCashBox() || supervises()),
      // Receipts in arrears by age and their collection efforts (#315).
      item('cartera', 'Cartera vencida', 'clock', () =>
        has('agency_director', 'agency_admin', 'agency_manager', 'collections_officer'),
      ),
      item('aseguradoras', 'Remesas y conciliación', 'scale', supervises),
      item('comisiones', () => (own() ? 'Mis comisiones' : 'Comisiones'), 'receipt', ownOrAll),
      // The commission side of the portfolio (not the collections officer's).
      item('liquidaciones', 'Liquidaciones', 'settle', () =>
        has('agency_director', 'agency_admin', 'agency_manager', 'cashier', 'agent'),
      ),
    ],
  },
  {
    id: 'aseguradoras',
    label: 'Aseguradoras y productos',
    icon: 'building',
    items: [
      // The carriers' plans and coverages and each carrier's profile (#307): whoever sells or
      // manages; the matrix and the rules inside it stay the direction's and administration's.
      item('productos', 'Productos y aseguradoras', 'layers', () =>
        has('agency_director', 'agency_admin', 'agency_manager', 'agent'),
      ),
      // How Nubi works with Seguros G&T: sandbox, operations, calls and pending parameters (#215).
      item('integracion-gyt', 'Integración con G&T', 'bolt', supervises),
      item('conocimiento', 'Conocimiento', 'book', canAskKnowledge),
      item('ordenes', 'Órdenes de trabajo', 'inbox', sells),
    ],
  },
  {
    id: 'equipo',
    label: 'Equipo y capacitación',
    icon: 'team',
    items: [
      // The agency's people, their roles, baja and profile (#313): direction, administration and
      // the agency manager (reading).
      item('equipo', 'Equipo de la agencia', 'team', () =>
        has('agency_director', 'agency_admin', 'agency_manager'),
      ),
      item('onboarding', 'Onboarding', 'users', followsOnboarding),
      item('antecedentes', 'Antecedentes', 'shield', seesBackgroundChecks),
      item('cursos', 'Cursos', 'cap', buildsCourses),
    ],
  },
  {
    id: 'configuracion',
    label: 'Configuración',
    icon: 'sliders',
    items: [
      item('boleta', 'Boleta de onboarding', 'file', supervises),
      item('recordatorios', 'Recordatorios', 'bell', supervises),
    ],
  },
];

/**
 * «Administración de Nubi» (ADR-0023): the menu of Nubi's own organization, what belongs to the
 * platform and to no agency: its agencies, its carriers, the people of Nubi and the
 * administration log.
 */
export const PLATFORM_NAV: NavGroup[] = [
  {
    id: 'administracion-nubi',
    label: 'Administración de Nubi',
    icon: 'shield',
    items: [
      item('agencias', 'Agencias', 'building', administersNubi),
      // The carriers of the platform and their status (#283, ADR-0027). Not `aseguradoras`: that
      // is the agency's «Remesas y conciliación».
      item('aseguradoras-nubi', 'Aseguradoras', 'layers', administersNubi),
      // The notice to every agency (#284, ADR-0028). Not `avisos`: that is a person's inbox.
      item('aviso-global', 'Aviso a las agencias', 'bell', administersNubi),
      // Each agency's peripheral modules, on or off (#285, ADR-0029).
      item('modulos-agencia', 'Módulos por agencia', 'sliders', administersNubi),
      // The people of Nubi's own organization and the whole administration log (#274).
      item('administradores', 'Administradores', 'users', administersNubi),
      item('bitacora', 'Bitácora', 'book', administersNubi),
    ],
  },
];

/** The menu of the signed-in person: Nubi's own for its organization, the agency's otherwise. */
export const menuModel = (): NavGroup[] => (isNubiOrg() ? PLATFORM_NAV : NAV);
/** Every group of both models (the markup holds them all; each person sees one model). */
const ALL_GROUPS = [...NAV, ...PLATFORM_NAV];

/** The group a screen belongs to (none for Avisos and for screens outside the menu). */
export const groupOf = (view: string) =>
  ALL_GROUPS.find((g) => g.items.some((i) => i.view === view));

/** What the signed-in person sees in the menu, as it reads: the options outside and each group. */
export function menuFor(): { top: string[]; groups: { label: string; items: string[] }[] } {
  const shown = (items: NavItem[]) => items.filter((i) => i.sees()).map((i) => i.label());
  return {
    top: shown(TOP),
    groups: menuModel()
      .map((g) => ({ label: g.label, items: shown(g.items) }))
      .filter((g) => g.items.length > 0),
  };
}

/** An option of the person's menu by its screen. */
const itemOf = (view: string) =>
  [...TOP, ...menuModel().flatMap((g) => g.items)].find((i) => i.view === view);

/**
 * Each role's main destination, the screen it opens most (#237): in the phone's top bar beside
 * Avisos, so the day's work is one tap away while the rest of the menu waits in its sheet.
 * Management reads the portfolio, the agent quotes, the cashier collects; evaluation and training
 * have one screen each. The first rule that applies to the person, among the options they see.
 */
const MAIN: [string, () => boolean][] = [
  ['agencias', administersNubi],
  ['resumen', supervises],
  ['cotizar', () => has('agent')],
  ['caja', () => has('cashier')],
  ['resumen', seesPortfolio],
  ['antecedentes', () => has('evaluator')],
  ['cursos', () => has('training_manager')],
];

export function mainView(): NavItem | null {
  for (const [view, applies] of MAIN) {
    const i = itemOf(view);
    if (applies() && i?.sees()) return i;
  }
  return null;
}

/**
 * The phone's top bar (#237), from the same model: Avisos with its count (a bell; its name stays
 * for screen readers) and the person's main destination. The rest is in the menu's sheet.
 */
export function quickHtml(): string {
  const main = mainView();
  // Only Avisos of the options at the top: «Mis tareas» waits in the menu's sheet.
  return [...TOP.filter((i) => i.count && i.sees()), ...(main ? [main] : [])]
    .map(
      (i) =>
        `<button type="button" class="quick${i.count ? ' quick-icon' : ''}" data-view="${esc(i.view)}">${icon(i.icon)}<span class="nav-text${i.count ? ' sr-only' : ''}">${esc(i.label())}</span>${
          i.count ? '<span class="count" hidden>0</span>' : ''
        }</button>`,
    )
    .join('');
}

const button = (i: NavItem) =>
  `<button type="button" data-view="${esc(i.view)}" hidden>${icon(i.icon)}<span class="nav-text">${esc(i.label())}</span>${
    i.count ? '<span class="count" hidden>0</span>' : ''
  }</button>`;

/** The menu's markup: every option hidden until `paintNav` shows the person their part. */
export function navHtml(): string {
  const groups = ALL_GROUPS.map(
    (g) => `<div class="nav-group" data-nav-group="${g.id}" hidden>
      <button type="button" class="nav-toggle" id="nav-toggle-${g.id}" aria-expanded="false" aria-controls="nav-group-${g.id}" data-nav-toggle="${g.id}">${icon(g.icon, 'icon group-icon')}<span class="nav-toggle-text">${esc(g.label)}</span>${icon('chevron', 'icon chevron')}</button>
      <div class="nav-items" id="nav-group-${g.id}" role="group" aria-labelledby="nav-toggle-${g.id}">${g.items.map(button).join('')}</div>
    </div>`,
  );
  return `<div class="nav-top">${TOP.map(button).join('')}</div>${groups.join('')}`;
}

/**
 * Shows the signed-in person their part of the menu: their options, and only the groups that
 * have any. Every group starts closed (the router opens the one of the screen on view).
 */
export function paintNav(nav: HTMLElement) {
  const paint = (i: NavItem, shown: boolean) => {
    const b = nav.querySelector<HTMLButtonElement>(`[data-view="${i.view}"]`);
    if (!b) return;
    b.hidden = !shown;
    const text = b.querySelector('.nav-text');
    if (text) text.textContent = i.label();
  };
  for (const i of TOP) paint(i, i.sees());
  // Only the person's model: Nubi's organization never sees an agency's options, nor the reverse.
  const model = menuModel();
  for (const g of ALL_GROUPS) {
    const mine = model.includes(g);
    for (const i of g.items) paint(i, mine && i.sees());
    const el = nav.querySelector<HTMLElement>(`[data-nav-group="${g.id}"]`);
    if (!el) continue;
    el.hidden = !(mine && g.items.some((i) => i.sees()));
    setOpen(el, false);
  }
}

function setOpen(group: HTMLElement, open: boolean) {
  group.querySelector('.nav-toggle')?.setAttribute('aria-expanded', String(open));
}

/** Opens the group of the screen on view and marks it (a closed group still shows it holds it). */
export function revealView(nav: ParentNode, view: string) {
  for (const g of nav.querySelectorAll<HTMLElement>('.nav-group[data-current]'))
    delete g.dataset.current;
  // The option inside its group: the phone's top bar (#237) holds a copy of some, outside any group.
  const group = nav
    .querySelector(`.nav-group [data-view="${view}"]`)
    ?.closest<HTMLElement>('.nav-group');
  if (!group) return;
  group.dataset.current = '';
  setOpen(group, true);
  // The screen's option in sight, even when the menu was scrolled away from it (#190).
  showInMenu(group.querySelector(`[data-view="${view}"]`));
}

/** Where focus goes in the menu for a key: the next or previous option (around), or an end. */
export function moveFocus(key: string, at: number, count: number): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return (at + 1) % count;
    case 'ArrowUp':
    case 'ArrowLeft':
      return (at - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** The sidebar as the menu needs it: whether it is folded into the rail, and opening it. */
export type SidebarControl = { collapsed: () => boolean; expand: () => void };

/**
 * The group buttons open and close their options; the arrows, Home and End move through the menu.
 * In the collapsed sidebar's rail a group's button opens the sidebar with that group open (#188):
 * a flyout would be a second menu to make accessible (its own focus, Escape, positioning out of the
 * scrolling menu); opening the sidebar reuses the disclosure the person already knows.
 */
export function wireNav(nav: HTMLElement, sidebar?: SidebarControl) {
  nav.addEventListener('click', (ev) => {
    const toggle = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-nav-toggle]');
    const group = toggle?.closest<HTMLElement>('.nav-group');
    if (!toggle || !group) return;
    if (sidebar?.collapsed()) {
      sidebar.expand();
      setOpen(group, true);
    } else setOpen(group, toggle.getAttribute('aria-expanded') !== 'true');
    // A group opened near the bottom shows its options instead of opening out of sight.
    if (toggle.getAttribute('aria-expanded') === 'true') showInMenu(group);
  });
  // Whatever the focus reaches (Tab, the arrows, a click) is seen whole, clear of the fade.
  nav.addEventListener('focusin', (ev) => showInMenu(ev.target as Element | null));
  nav.addEventListener('keydown', (ev) => {
    if (ev.altKey || ev.ctrlKey || ev.metaKey) return;
    // Only the buttons a person can reach now: not the hidden ones nor those of closed groups.
    const buttons = [...nav.querySelectorAll<HTMLButtonElement>('button')].filter(
      (b) => b.getClientRects().length > 0,
    );
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    const to = moveFocus(ev.key, at, buttons.length);
    if (to === null) return;
    ev.preventDefault();
    buttons[to]?.focus();
  });
}
